import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  NormalizedTextBox,
  PageTextIndex,
  TextFocusSelector,
  TextFocusService,
  TextUnit,
} from '../../../../../shared/text-focus.service';
import { ChatStateService } from '../../../../../services/chat-state.service';
import { SelectedTextBoundingBox, SelectedTextContext } from '../../../../../models/chat.models';
import { altKeyName } from '../alt-key-name';

/** The selectable layer's units in layout order, and each unit index's place in it. */
interface SelectableArrangement {
  units: { unit: TextUnit; index: number }[];
  placeOf: Map<number, number>;
}

/** A word's rectangle on screen, in client pixels. */
type WordRect = Pick<DOMRect, 'left' | 'top' | 'right' | 'bottom'>;

/** A mouse drag that began on a word, which the layer extends itself (Issue #3336). */
interface WordDrag {
  /** The spans in layout order, as the layer lays them out. */
  spans: HTMLSpanElement[];
  /** Where in `spans` the drag began. */
  anchor: number;
  startX: number;
  startY: number;
  x: number;
  y: number;
  /** Covers the window while the drag runs, so the cursor stays a text cursor over the gaps. */
  shield: HTMLDivElement | null;
  frame: number | null;
  /** The words' rectangles, measured once and again only after the view scrolls or zooms. */
  rects: WordRect[] | null;
  /** The element the drawing scrolls in, which the drag scrolls at its edges. */
  scroller: HTMLElement | null;
}

/**
 * Text selection overlay rendering transparent selectable OCR/PDF text spans aligned with the canvas.
 *
 * Supports:
 * - Mouse selection across words/lines in reading order, block by block where the server sent a
 *   layout order (Issue #3293); a drag from a word follows the pointer across blank gaps (Issue #3336)
 * - Standard clipboard copy (Ctrl+C / Cmd+C / action bar)
 * - Contextual action bar offering "Copy" and "Use in chat"
 * - Attribution with file, page, region path, bounding boxes, and reproducible TextFocusSelector
 * - Synchronization with zoom/pan gesture transforms
 */
@Component({
  selector: 'app-text-selection-overlay',
  imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule],
  templateUrl: './text-selection-overlay.component.html',
  styleUrls: ['./text-selection-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextSelectionOverlayComponent {
  @ViewChild('layerContainer') private layerContainer?: ElementRef<HTMLDivElement>;

  @Input() pageIndex: PageTextIndex | null = null;
  @Input() projectId: string = '';
  @Input() fileId: string = '';
  @Input() fileName: string = '';
  @Input() pageNumber: number = 0;
  @Input() canvasWidth: number = 0;
  @Input() canvasHeight: number = 0;
  @Input() canvasScale: number = 1;
  @Input() canvasOffsetX: number = 0;
  @Input() canvasOffsetY: number = 0;
  @Input() disabled: boolean = false;

  @Output() selectionAction = new EventEmitter<{
    action: 'copy' | 'use-in-chat';
    quote: SelectedTextContext;
  }>();

  private snackBar = inject(MatSnackBar);
  private chatState = inject(ChatStateService);
  private textFocus = inject(TextFocusService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private readonly onDocumentKeydownListener = (event: KeyboardEvent): void => this.onKeydown(event);

  constructor() {
    if (typeof window === 'undefined') return;
    // Escape must be cancelable while a selection is active. Use the window capture phase so
    // Zone.js does not coalesce this listener into an existing passive document-keydown callback.
    window.addEventListener('keydown', this.onDocumentKeydownListener, { capture: true, passive: false });
    this.destroyRef.onDestroy(() => {
      window.removeEventListener('keydown', this.onDocumentKeydownListener, { capture: true });
      this.endWordDrag(false);
    });
  }

  hasActiveSelection = false;
  /**
   * How to select a single word, now that a plain double-click zooms the page (Issue #3312).
   * Shown with the selection's actions and read out with the layer's label.
   */
  readonly wordSelectHint = `${altKeyName()} + double-click selects a word`;
  /** How to select text by dragging: a plain drag pans the page (Issue #3338). */
  readonly dragSelectHint = `Hold ${altKeyName()} and drag to select text`;
  actionBarPosition: { x: number; y: number } | null = null;

  private arrangedFor: PageTextIndex | null | undefined;
  private arranged: SelectableArrangement = { units: [], placeOf: new Map() };

  /**
   * The units in the order the selectable layer lays them out. A browser selection covers every
   * span between the first and last selected one in that order, so where the server sent a layout
   * order (Issue #3293) the spans follow it and a drag stays inside one block of the sheet. Units
   * without one (text read from the PDF itself) follow, in index order. `data-unit-index` keeps each
   * unit's index, which search and highlight links use.
   */
  get selectableUnits(): { unit: TextUnit; index: number }[] {
    return this.arrangement().units;
  }

  private arrangement(): SelectableArrangement {
    if (this.arrangedFor !== this.pageIndex) {
      this.arrangedFor = this.pageIndex;
      const units = (this.pageIndex?.units ?? []).map((unit, index) => ({ unit, index }));
      if (units.some(entry => entry.unit.layoutOrder)) {
        units.sort((a, b) => (a.unit.layoutOrder ?? Infinity) - (b.unit.layoutOrder ?? Infinity) || a.index - b.index);
      }
      this.arranged = { units, placeOf: new Map(units.map((entry, place) => [entry.index, place])) };
    }
    return this.arranged;
  }
  currentSelectedText = '';
  currentQuote: SelectedTextContext | null = null;

  getDisplayBox(box: NormalizedTextBox): NormalizedTextBox {
    const isNormalized = this.pageIndex?.normalizedBoxes ?? true;
    const sourceWidth = isNormalized ? this.canvasWidth : this.canvasScale;
    const sourceHeight = isNormalized ? this.canvasHeight : this.canvasScale;
    return {
      x: box.x * sourceWidth,
      y: box.y * sourceHeight,
      width: Math.max(0, box.width * sourceWidth),
      height: Math.max(0, box.height * sourceHeight),
      rotationDegrees: box.rotationDegrees,
    };
  }

  rotation(box: NormalizedTextBox): string | null {
    return box.rotationDegrees ? `rotate(${box.rotationDegrees}deg)` : null;
  }

  private measureCtx?: CanvasRenderingContext2D | null;

  private getMeasureContext(): CanvasRenderingContext2D | null {
    if (this.measureCtx !== undefined) return this.measureCtx;
    if (typeof document === 'undefined') {
      this.measureCtx = null;
      return null;
    }
    try {
      const canvas = document.createElement('canvas');
      this.measureCtx = canvas.getContext('2d');
    } catch {
      this.measureCtx = null;
    }
    return this.measureCtx;
  }

  /** Font size at which each word is measured once; its width grows in proportion with the size. */
  private static readonly MEASURE_FONT_PX = 100;
  /** Upper bound on remembered words, so a long session on many pages cannot grow it without limit. */
  private static readonly MAX_MEASURED_TEXTS = 20_000;
  /** Natural width of each text at 1px font size. */
  private readonly widthPerFontPx = new Map<string, number>();

  /**
   * Natural width of a text per pixel of font size, measured once per text. The template asks for
   * every word's scale on each change-detection pass (thousands of words on a drawing sheet), and
   * re-measuring them all with measureText was a large share of each pass while a panel opened
   * or closed (Issue #3243).
   */
  private measureWidthPerFontPx(ctx: CanvasRenderingContext2D, text: string): number {
    const known = this.widthPerFontPx.get(text);
    if (known !== undefined) return known;
    ctx.font = `${TextSelectionOverlayComponent.MEASURE_FONT_PX}px sans-serif`;
    const perPx = ctx.measureText(text).width / TextSelectionOverlayComponent.MEASURE_FONT_PX;
    if (this.widthPerFontPx.size >= TextSelectionOverlayComponent.MAX_MEASURED_TEXTS) {
      // Drop only the oldest entry (a Map keeps insertion order), so reaching the limit never
      // makes the words of the page on screen all re-measure in one pass.
      const oldest = this.widthPerFontPx.keys().next().value;
      if (oldest !== undefined) this.widthPerFontPx.delete(oldest);
    }
    this.widthPerFontPx.set(text, perPx);
    return perPx;
  }

  getScaleX(text: string, width: number, height: number): number {
    if (!text || width <= 0 || height <= 0) return 1;
    const ctx = this.getMeasureContext();
    if (ctx) {
      try {
        const naturalWidth = this.measureWidthPerFontPx(ctx, text) * height;
        if (naturalWidth > 0) {
          return width / naturalWidth;
        }
      } catch {
        // Fall back to proportional font estimation
      }
    }
    // Proportional sans-serif font average glyph width (~0.52 * height)
    const estimatedWidth = text.length * height * 0.52;
    if (estimatedWidth > 0) {
      return width / estimatedWidth;
    }
    return 1;
  }

  scaleTransform(box: NormalizedTextBox, text: string): string | null {
    const scaleX = this.getScaleX(text, box.width, box.height);
    if (Math.abs(scaleX - 1) < 0.001) return null;
    return `scaleX(${scaleX.toFixed(4)})`;
  }

  transform(box: NormalizedTextBox, text: string): string {
    const rot = this.rotation(box);
    const scale = this.scaleTransform(box, text);
    if (rot && scale) return `${rot} ${scale}`;
    return rot || scale || '';
  }

  setGestureTransform(transform: string, transformOrigin: string, transition: string): void {
    const container = this.layerContainer?.nativeElement;
    if (!container) return;
    container.style.transform = transform;
    container.style.transformOrigin = transformOrigin;
    container.style.transition = transition;
  }

  @HostListener('document:selectionchange')
  onSelectionChange(): void {
    this.updateSelection();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.hasActiveSelection) {
      this.clearSelection();
      event.preventDefault();
      return;
    }
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'c' && this.hasActiveSelection) {
      void this.copySelectedText();
    }
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('.selection-action-bar')) {
      return;
    }
    if (!target?.closest('.text-selection-layer') && this.hasActiveSelection) {
      this.clearSelection();
    }
    this.startWordDrag(event);
  }

  /** How far the mouse must move from the press before a drag selects (a shorter move is a click). */
  private static readonly DRAG_START_PX = 3;

  /** How close to the edge of the scrolling view the pointer must come to scroll it. */
  private static readonly EDGE_SCROLL_PX = 24;
  /** The fastest the view scrolls during a drag, per frame. */
  private static readonly MAX_SCROLL_STEP_PX = 30;

  private drag: WordDrag | null = null;
  private readonly onDragMove = (event: MouseEvent): void => this.moveWordDrag(event);
  private readonly onDragEnd = (): void => this.endWordDrag();
  private readonly onDragViewChange = (): void => {
    if (this.drag) this.drag.rects = null;
  };

  /**
   * A drawing has wide blank gaps between its words. Left to the browser, a drag selection only
   * moves when the pointer is over a word, so the highlight lagged behind the pointer (Issue
   * #3336). So an Alt (Option) + press on a word, the text tool's drag (Issue #3338), is taken
   * over: on each move the selection runs, word by word in layout order, from the pressed word to
   * the word nearest the pointer. A plain press never gets here, because the preview pans with it;
   * the second press of Alt + double-click is left to the browser, which selects the word.
   */
  private startWordDrag(event: MouseEvent): void {
    this.endWordDrag();
    // Shift + press extends the current selection, which the browser already does well.
    if (this.disabled || event.button !== 0 || !event.altKey || event.shiftKey || event.detail > 1) return;
    // A touch screen emulates mouse events after a tap, which the touch handlers already handled.
    if (
      (event as MouseEvent & { sourceCapabilities?: { firesTouchEvents?: boolean } }).sourceCapabilities
        ?.firesTouchEvents
    ) {
      return;
    }
    const container = this.layerContainer?.nativeElement;
    const word = (event.target as Element | null)?.closest?.('.text-unit-span');
    if (!container || !(word instanceof HTMLSpanElement) || !container.contains(word)) return;
    const spans = Array.from(container.querySelectorAll<HTMLSpanElement>('.text-unit-span'));
    const anchor = spans.indexOf(word);
    if (anchor < 0) return;
    // Without this the browser starts its own drag selection, which would fight this one.
    event.preventDefault();
    window.getSelection()?.removeAllRanges();
    this.drag = {
      spans,
      anchor,
      startX: event.clientX,
      startY: event.clientY,
      x: event.clientX,
      y: event.clientY,
      shield: null,
      frame: null,
      rects: null,
      scroller: TextSelectionOverlayComponent.scrollingAncestor(container),
    };
    document.addEventListener('mousemove', this.onDragMove, true);
    document.addEventListener('mouseup', this.onDragEnd, true);
    // A scroll or a wheel zoom during the drag moves the words, so their rectangles are re-measured.
    document.addEventListener('scroll', this.onDragViewChange, true);
    document.addEventListener('wheel', this.onDragViewChange, true);
  }

  private moveWordDrag(event: MouseEvent): void {
    const drag = this.drag;
    if (!drag) return;
    if (!(event.buttons & 1)) {
      this.endWordDrag();
      return;
    }
    drag.x = event.clientX;
    drag.y = event.clientY;
    if (!drag.shield) {
      const moved = Math.max(Math.abs(drag.x - drag.startX), Math.abs(drag.y - drag.startY));
      if (moved < TextSelectionOverlayComponent.DRAG_START_PX) return;
      drag.shield = document.createElement('div');
      drag.shield.className = 'text-drag-shield';
      // Stacking contract: the cover exists only between the drag's first move and its release, and
      // must sit above everything on the page (toolbars, overlays, this layer at 1050) so that no
      // other element's cursor shows during the drag. Nothing else should ever be placed above it.
      Object.assign(drag.shield.style, { position: 'fixed', inset: '0', zIndex: '2147483647', cursor: 'text' });
      document.body.appendChild(drag.shield);
    }
    this.scheduleWordDrag(drag);
  }

  /** Update the selection, and scroll at the view's edges, on the next frame (once per frame). */
  private scheduleWordDrag(drag: WordDrag): void {
    if (drag.frame !== null) return;
    drag.frame = requestAnimationFrame(() => {
      drag.frame = null;
      if (this.drag !== drag) return;
      if (this.scrollAtEdge(drag)) {
        drag.rects = null;
        // Keep scrolling while the pointer is held at the edge, even if the mouse does not move.
        this.scheduleWordDrag(drag);
      }
      this.extendWordDrag(drag);
    });
  }

  /**
   * Taking the drag over from the browser also turned off its scrolling when a selection is dragged
   * to the edge of the view, so the drag scrolls the view itself, faster the further past the edge
   * the pointer goes. Returns whether it scrolled.
   */
  private scrollAtEdge(drag: WordDrag): boolean {
    const scroller = drag.scroller;
    if (!scroller) return false;
    const view = scroller.getBoundingClientRect();
    const step = (before: number, after: number): number => {
      const edge = TextSelectionOverlayComponent.EDGE_SCROLL_PX;
      const into = Math.max(edge - before, 0) - Math.max(edge - after, 0);
      if (into === 0) return 0;
      const max = TextSelectionOverlayComponent.MAX_SCROLL_STEP_PX;
      return Math.sign(into) * Math.min(max, Math.ceil(Math.abs(into) / 2));
    };
    const dx = -step(drag.x - view.left, view.right - drag.x);
    const dy = -step(drag.y - view.top, view.bottom - drag.y);
    const { scrollLeft, scrollTop } = scroller;
    if (dx || dy) scroller.scrollBy(dx, dy);
    return scroller.scrollLeft !== scrollLeft || scroller.scrollTop !== scrollTop;
  }

  /** The nearest ancestor that scrolls, if any. */
  private static scrollingAncestor(element: HTMLElement): HTMLElement | null {
    for (let node = element.parentElement; node; node = node.parentElement) {
      const { overflow, overflowX, overflowY } = getComputedStyle(node);
      if (/(auto|scroll)/.test(`${overflow} ${overflowX} ${overflowY}`)) return node;
    }
    return null;
  }

  /** Select from the pressed word to the word nearest the pointer, whole words at both ends. */
  private extendWordDrag(drag: WordDrag): void {
    drag.rects ??= drag.spans.map(span => span.getBoundingClientRect());
    const target = TextSelectionOverlayComponent.nearestWord(drag.rects, drag.x, drag.y);
    if (target < 0) return;
    const text = (span: HTMLSpanElement): Node => span.querySelector('.text-unit-glyphs')?.firstChild ?? span;
    const length = (node: Node): number =>
      node.nodeType === Node.TEXT_NODE ? (node.textContent ?? '').length : node.childNodes.length;
    const from = text(drag.spans[drag.anchor]);
    const to = text(drag.spans[target]);
    const forward = target >= drag.anchor;
    window.getSelection()?.setBaseAndExtent(from, forward ? 0 : length(from), to, forward ? length(to) : 0);
  }

  /** End the drag. `apply` is false when the layer is going away and its words with it. */
  private endWordDrag(apply = true): void {
    const drag = this.drag;
    if (!drag) return;
    this.drag = null;
    if (drag.frame !== null) cancelAnimationFrame(drag.frame);
    // The last move may still be waiting for its frame; apply it so the release point counts.
    if (drag.shield) {
      if (apply) this.extendWordDrag(drag);
      drag.shield.remove();
    }
    document.removeEventListener('mousemove', this.onDragMove, true);
    document.removeEventListener('mouseup', this.onDragEnd, true);
    document.removeEventListener('scroll', this.onDragViewChange, true);
    document.removeEventListener('wheel', this.onDragViewChange, true);
  }

  /**
   * The word nearest a point: the one whose rectangle is closest to it, or that contains it. On a
   * tie, the earlier word in layout order wins. Returns -1 when there are no words.
   */
  static nearestWord(rects: WordRect[], x: number, y: number): number {
    let best = -1;
    let bestDistance = Infinity;
    rects.forEach((rect, index) => {
      const dx = Math.max(rect.left - x, 0, x - rect.right);
      const dy = Math.max(rect.top - y, 0, y - rect.bottom);
      const distance = dx * dx + dy * dy;
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });
    return best;
  }

  updateSelection(): void {
    if (this.disabled || !this.pageIndex || !this.pageIndex.units.length) {
      if (this.hasActiveSelection) {
        this.hasActiveSelection = false;
        this.actionBarPosition = null;
        this.cdr.markForCheck();
      }
      return;
    }

    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !selection.rangeCount) {
      if (this.hasActiveSelection) {
        this.hasActiveSelection = false;
        this.actionBarPosition = null;
        this.cdr.markForCheck();
      }
      return;
    }

    const range = selection.getRangeAt(0);
    const container = this.layerContainer?.nativeElement;
    if (!container) return;

    const startInside = container.contains(range.startContainer);
    const endInside = container.contains(range.endContainer);
    if (!startInside && !endInside) {
      if (this.hasActiveSelection) {
        this.hasActiveSelection = false;
        this.actionBarPosition = null;
        this.cdr.markForCheck();
      }
      return;
    }

    const spans = container.querySelectorAll<HTMLSpanElement>('.text-unit-span');
    const selectedUnits: { unit: TextUnit; index: number }[] = [];

    spans.forEach(span => {
      if (selection.containsNode(span, true)) {
        const idx = parseInt(span.getAttribute('data-unit-index') || '-1', 10);
        if (idx >= 0 && idx < this.pageIndex!.units.length) {
          selectedUnits.push({ unit: this.pageIndex!.units[idx], index: idx });
        }
      }
    });

    // Copied text reads in the layer's order: block by block where the server sent a layout order.
    const placeOf = this.arrangement().placeOf;
    selectedUnits.sort((a, b) => (placeOf.get(a.index) ?? a.index) - (placeOf.get(b.index) ?? b.index));

    let cleanText = '';
    if (selectedUnits.length > 0) {
      let lastY: number | null = null;
      for (const item of selectedUnits) {
        const u = item.unit;
        const currentY = u.box.y;
        if (lastY !== null && Math.abs(currentY - lastY) > u.box.height * 0.7) {
          cleanText += '\n' + u.text;
        } else {
          cleanText += (cleanText ? ' ' : '') + u.text;
        }
        lastY = currentY;
      }
      cleanText = cleanText.trim();
    } else {
      cleanText = selection.toString().trim();
    }

    if (!cleanText) {
      this.hasActiveSelection = false;
      this.actionBarPosition = null;
      this.cdr.markForCheck();
      return;
    }

    this.currentSelectedText = cleanText;

    const boundingBoxes: SelectedTextBoundingBox[] = selectedUnits.map(su => ({
      x: su.unit.box.x,
      y: su.unit.box.y,
      width: su.unit.box.width,
      height: su.unit.box.height,
      rotationDegrees: su.unit.box.rotationDegrees,
    }));

    const regionPath = selectedUnits.find(su => su.unit.regionPath)?.unit.regionPath;
    const excerpt = cleanText.length > 120 ? cleanText.slice(0, 117) + '...' : cleanText;

    let selector: TextFocusSelector | undefined;
    if (this.pageIndex && cleanText.length > 0 && cleanText.length <= 256) {
      try {
        selector = this.textFocus.buildSelectorForExact(cleanText, this.pageIndex);
      } catch {
        // Leave undefined if selector construction fails
      }
    }

    this.currentQuote = {
      projectId: this.projectId,
      fileId: this.fileId,
      fileName: this.fileName,
      pageNumber: this.pageNumber,
      selectedText: cleanText,
      excerpt,
      regionPath,
      boundingBoxes: boundingBoxes.length > 0 ? boundingBoxes : undefined,
      selector,
    };

    const rangeRect = range.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const barWidth = 200;
    const barHeight = 40;
    let x = rangeRect.left - containerRect.left + rangeRect.width / 2 - barWidth / 2;
    let y = rangeRect.top - containerRect.top - barHeight - 8;

    if (y < 4) {
      y = rangeRect.bottom - containerRect.top + 8;
    }

    x = Math.max(8, Math.min(x, container.clientWidth - barWidth - 8));

    this.actionBarPosition = { x, y };
    this.hasActiveSelection = true;
    this.cdr.markForCheck();
  }

  async copySelectedText(): Promise<void> {
    if (!this.currentSelectedText) return;
    let copied = false;
    if (navigator?.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(this.currentSelectedText);
        copied = true;
      } catch {
        copied = false;
      }
    }
    if (!copied && typeof document !== 'undefined') {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = this.currentSelectedText;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.top = '0';
        textarea.style.left = '0';
        textarea.style.width = '2em';
        textarea.style.height = '2em';
        textarea.style.padding = '0';
        textarea.style.border = 'none';
        textarea.style.outline = 'none';
        textarea.style.boxShadow = 'none';
        textarea.style.background = 'transparent';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        copied = document.execCommand('copy');
        document.body.removeChild(textarea);
      } catch {
        copied = false;
      }
    }
    if (copied) {
      this.snackBar.open('Copied to clipboard', undefined, { duration: 2000 });
      if (this.currentQuote) {
        this.selectionAction.emit({ action: 'copy', quote: this.currentQuote });
      }
    } else {
      this.snackBar.open('Unable to copy text to clipboard', 'Dismiss', { duration: 3000 });
    }
  }

  useInChat(): void {
    if (!this.currentQuote) return;
    const quote = { ...this.currentQuote };
    this.selectionAction.emit({ action: 'use-in-chat', quote });
    this.chatState.addSelectedText(quote);
    this.chatState.openChat();
    this.clearSelection();
  }

  clearSelection(): void {
    const selection = window.getSelection();
    if (selection && typeof selection.removeAllRanges === 'function') {
      selection.removeAllRanges();
    }
    this.hasActiveSelection = false;
    this.actionBarPosition = null;
    this.currentQuote = null;
    this.currentSelectedText = '';
    this.cdr.markForCheck();
  }
}
